import React from "react";
import styled from "styled-components";
import DatePicker from "react-datepicker";
import { getMomentDate } from "../shared/TimeUtil";
import GroupsDropDown from "../sharedComponents/GroupsDropDown";
import SingleSelectDropDown from "../sharedComponents/SingleSelectDropDown";
import { ReactComponent as ArrowDownIcon } from "../svgs/ArrowDownIcon.svg";

import "react-datepicker/dist/react-datepicker.css";

const ConsultantRow = styled.tr`
  &:nth-child(2n) {
    background: var(--table-band-background);
  }
  &:hover {
    background: var(--table-hover-background);
  }
`;

const ConsultantData = styled.td`
  padding: 0.5rem 0;
  text-align: left;
  width: max-content;
`;

const ConsultantNameInput = styled.input`
  font-size: 1rem;
  border: 0;
  outline: none;
  background: transparent;
  text-align: left;
  width: 100%;
`;

const DropDownContainer = styled.div`
  position: relative;
`;

const GroupsLabel = styled.label`
  cursor: pointer;
  width: 100%;
  font-style: ${props => (props.empty ? "italic" : "normal")};
  color: ${props => (props.empty ? "gray" : "var(--color-grey)")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SelectContainer = styled.div`
  width: 60%;
  display: grid;
  grid-template-columns: minmax(90%, max-content) 1fr;
  align-items: center;
  border: 1px solid transparent;
  border-color: ${props =>
    props.selected ? "var(--color-grey)" : "transparent"};
  padding: 0.5rem 0.7rem;
  border-bottom: none;
  cursor: ${props => (props.group ? "pointer" : "default")};

  & svg {
    ${props => (props.selected ? "transform: rotate(180deg);" : "")};
    justify-self: end;
    margin-left: 0.5rem;
  }
`;

const ArrowDownIconStyled = styled(ArrowDownIcon)`
  height: 8px;
  width: 12px;

  & * {
    fill: var(--color-grey);
  }
`;

const AdminCheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  width: max-content;

  > input {
    visibility: hidden;
    height: 1px;
    width: 1px;
    position: absolute;
  }

  > input:checked + div {
    background: var(--theme-active-indicator);

    > svg {
      visibility: visible;
    }
  }

  &[data-disabled="true"] {
    cursor: default;
    opacity: 0.6;
  }
`;

const AdminStyledCheckbox = styled.div`
  width: 1.1rem;
  height: 1.1rem;
  border-radius: 0.2rem;
  background-color: white;
  border: 1.5px solid var(--color-lightergrey);
  display: inline-block;
  transition: all 150ms;
`;

const AdminCheckboxIcon = styled.svg`
  visibility: hidden;
  fill: none;
  stroke: white;
  stroke-width: 2px;
`;

const ConsultantRowItem = React.memo(function ConsultantRowItem({
  consultant,
  index,
  isAdmin,
  toggleAdmin,
  handleInputChange,
  departments,
  groups,
  seniorities,
  getDepartmentNameById,
  handleDepartmentSelect,
  getAllGroupsString,
  postConsultantGroupConnection,
  removeConsultantGroupConnection,
  getSeniorityNameById,
  handleSenioritySelect,
  handleStartDateChange,
  handleEndDateChange,
  handleSaveConsultant,
  linkToNewGroup
}) {
  const [depOpen, setDepOpen] = React.useState(false);
  const [groupOpen, setGroupOpen] = React.useState(false);
  const [seniorityOpen, setSeniorityOpen] = React.useState(false);

  const toggleDep = () => {
    setDepOpen(!depOpen);
    setGroupOpen(false);
    setSeniorityOpen(false);
  };
  const toggleGroups = () => {
    setGroupOpen(!groupOpen);
    setDepOpen(false);
    setSeniorityOpen(false);
  };
  const toggleSeniority = () => {
    setSeniorityOpen(!seniorityOpen);
    setDepOpen(false);
    setGroupOpen(false);
  };

  return (
    <ConsultantRow key={consultant.consultantId}>
      <ConsultantData>
        <ConsultantNameInput
          defaultValue={consultant.name}
          name="name"
          onBlur={e => handleInputChange(index, e)}
        />
      </ConsultantData>
      <ConsultantData>
        <ConsultantNameInput
          defaultValue={consultant.email}
          name="email"
          onBlur={e => handleInputChange(index, e)}
        />
      </ConsultantData>
      <ConsultantData>
        <SelectContainer selected={depOpen} onClick={toggleDep} group={true}>
          <GroupsLabel empty={!consultant.departmentId}>
            {getDepartmentNameById(consultant.departmentId)}
          </GroupsLabel>
          <ArrowDownIconStyled />
        </SelectContainer>
        {depOpen && (
          <DropDownContainer>
            <SingleSelectDropDown
              items={departments}
              selectedId={consultant.departmentId}
              onSelect={id => {
                handleDepartmentSelect(index, id);
                setDepOpen(false);
              }}
              onCloseDropdown={() => setDepOpen(false)}
            />
          </DropDownContainer>
        )}
      </ConsultantData>
      <ConsultantData>
        <SelectContainer
          selected={groupOpen}
          onClick={toggleGroups}
          group={true}
        >
          <GroupsLabel
            empty={!consultant.groups || consultant.groups.length === 0}
          >
            {getAllGroupsString(consultant)}
          </GroupsLabel>
          <ArrowDownIconStyled />
        </SelectContainer>
        {groupOpen && (
          <DropDownContainer>
            <GroupsDropDown
              groups={groups}
              selectedGroups={consultant.groups}
              onGroupSelect={groupId =>
                postConsultantGroupConnection(consultant.consultantId, groupId)
              }
              linkToNewGroup={linkToNewGroup}
              onGroupUnselect={groupId =>
                removeConsultantGroupConnection(
                  consultant.consultantId,
                  groupId
                )
              }
              onCloseDropdown={() => setGroupOpen(false)}
            />
          </DropDownContainer>
        )}
      </ConsultantData>
      <ConsultantData>
        <SelectContainer
          selected={seniorityOpen}
          onClick={toggleSeniority}
          group={true}
        >
          <GroupsLabel empty={!consultant.seniorityId}>
            {getSeniorityNameById(consultant.seniorityId)}
          </GroupsLabel>
          <ArrowDownIconStyled />
        </SelectContainer>
        {seniorityOpen && (
          <DropDownContainer>
            <SingleSelectDropDown
              items={[{ id: "", name: "" }].concat(seniorities)}
              selectedId={consultant.seniorityId || ""}
              onSelect={id => {
                handleSenioritySelect(index, id);
                setSeniorityOpen(false);
              }}
              onCloseDropdown={() => setSeniorityOpen(false)}
            />
          </DropDownContainer>
        )}
      </ConsultantData>
      <ConsultantData>
        <DatePicker
          className="datepicker-input"
          selected={
            consultant.startDate ? getMomentDate(consultant.startDate) : null
          }
          onChange={date => handleStartDateChange(date, index)}
          onBlur={() => handleSaveConsultant(index)}
          placeholderText="Velg dato"
        />
      </ConsultantData>
      <ConsultantData>
        <DatePicker
          className="datepicker-input"
          selected={
            consultant.endDate ? getMomentDate(consultant.endDate) : null
          }
          onChange={date => handleEndDateChange(date, index)}
          onBlur={() => handleSaveConsultant(index)}
          placeholderText="Velg dato"
        />
      </ConsultantData>
      <ConsultantData>
        <AdminCheckboxLabel data-disabled={!consultant.email}>
          <input
            type="checkbox"
            checked={isAdmin(consultant.email)}
            disabled={!consultant.email}
            onChange={e => toggleAdmin(consultant.email, e.target.checked)}
            aria-label="Gi/ta bort administrator-tilgang"
            title={
              consultant.email
                ? "Marker for å gi administrator-tilgang"
                : "Sett e-post først"
            }
          />
          <AdminStyledCheckbox>
            <AdminCheckboxIcon viewBox="0 0 24 24">
              <polyline points="20 6 9 17 4 12" />
            </AdminCheckboxIcon>
          </AdminStyledCheckbox>
        </AdminCheckboxLabel>
      </ConsultantData>
    </ConsultantRow>
  );
});

export default ConsultantRowItem;
